import { ModeForm } from "constants/enum";

import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Breadcrumbs as MUIBreadcrumbs, Stack } from "@mui/material";
import {
  Button,
  ClockFastForwardIcon,
  FormContainerControlled,
  Mail01Icon,
  Save01Icon,
  TextFieldHookForm,
  Typography,
} from "components";
import OTPInputHookForm from "components/OTPInput/OTPInput";
import { base, gray, primary } from "configs/colors";
import { shadows } from "configs/shadows";
import { confirmPasswordRequired, resetPasswordRequired } from "helpers/yup";
import { Modal } from "pages/Components";
import { UseFormReset, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

type Props = {
  mode?: ModeForm;
  email: string;
  isLoadingChangePassword: boolean;
  handleSubmitForm: (
    handleVerification: (csrfToken: string, timeCount: number) => void
  ) => void;
  handleSubmitChangePassword: (
    code: number,
    csrfToken: string,
    password: string,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    reset: UseFormReset<any>
  ) => void;
  errorMessage?: string;
};

const FormSchema = Yup.object({
  password: resetPasswordRequired,
  confirmPassword: confirmPasswordRequired,
});

export const ResetPasswordForm = (props: Props) => {
  const {
    mode,
    handleSubmitForm,
    handleSubmitChangePassword,
    email,
    isLoadingChangePassword,
    errorMessage,
  } = props;
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [csrfToken, setCsrfToken] = useState<string>("");
  const [timeCount, setTimeCount] = useState<number>(60);

  const methods = useForm<any>({
    values: {
      password: "",
      confirmPassword: "",
      code: "",
    },
    resolver: yupResolver(FormSchema),
  });
  const { watch, reset, setValue } = methods;
  const handleVerification = (data: string, count: number) => {
    setIsModalOpen(true);
    setCsrfToken(data);
    setTimeCount(count);
  };
  const onSubmit = (dataSubmit: any) => {
    handleSubmitForm(handleVerification);
  };

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (timeCount > 0 && isModalOpen) {
        setTimeCount(timeCount - 1);
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  }, [isModalOpen, timeCount, setTimeCount]);

  return (
    <FormContainerControlled methods={methods} onSubmit={onSubmit}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          height: "40px",
          marginBottom: "28px",
        }}
      >
        <MUIBreadcrumbs aria-label="breadcrumb">
          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: "600",
            }}
            color="text.primary"
          >
            Change Password
          </Typography>
        </MUIBreadcrumbs>
        <Box sx={{ alignSelf: "center" }}>
          <>
            <Button
              variant="outlined"
              sx={{
                marginRight: "8px",
              }}
              onClick={() => {
                navigate("/");
              }}
            >
              Back
            </Button>

            <Button type="submit" startIcon={<Save01Icon />}>
              Update
            </Button>
          </>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: "#F9FAFB",
          padding: "14px",
          borderRadius: "12px",
        }}
      >
        <Typography type="TextLG" fontWeight={600}>
          Information
        </Typography>
        <Grid container spacing="14px" marginTop="0">
          <Grid item xs={12} lg={4}>
            <TextFieldHookForm
              name="password"
              label="New Password"
              type="password"
              placeholder="Enter new password..."
              required
            />
          </Grid>
          <Grid item xs={12} lg={4} />
          <Grid item xs={12} lg={4} />
          <Grid item xs={12} lg={4}>
            <TextFieldHookForm
              name="confirmPassword"
              label="Re-confirm New Password"
              type="password"
              placeholder="Re-enter new password..."
              required
            />
          </Grid>
        </Grid>
      </Box>
      <Modal
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "30vw",
              maxWidth: "1300px", // Set your width here
            },
          },
        }}
        onClose={() => {
          setIsModalOpen(false);
          // setTimeCount(60);
          setValue("code", "");
        }}
        header={
          <>
            <Typography type="TextLG" fontWeight={600}>
              OTP Verification
            </Typography>
            <Typography type="TextSM" fontWeight={400}>
              Verification before updating
            </Typography>
          </>
        }
        open={isModalOpen}
        body={
          <Box display="flex" justifyContent="center">
            <Grid sx={{ width: "100%", maxWidth: "420px" }}>
              <Box display="flex" justifyContent="center" marginBottom="24px">
                <Box
                  display="flex"
                  borderRadius="8px"
                  border={`1px solid ${gray[300]}`}
                  boxShadow={shadows.xs}
                  sx={{ background: base.White }}
                  padding="14px"
                  width="56px"
                >
                  <Mail01Icon />
                </Box>
              </Box>

              <Typography
                type="DisplaySM"
                fontWeight={600}
                color={gray[900]}
                align="center"
              >
                Check your email
              </Typography>
              <Typography
                type="TextMD"
                color={gray[600]}
                marginTop={3}
                marginY="12px"
                align="center"
              >
                We sent an One Time Password to
              </Typography>
              <Typography
                type="TextMD"
                color={gray[600]}
                marginTop={3}
                align="center"
              >
                {email}
              </Typography>
              <Stack gap={6}>
                <OTPInputHookForm name="code" />
              </Stack>
              <Stack>
                <Button
                  size="lg"
                  useTypography
                  type="submit"
                  onClick={() => {
                    handleSubmitChangePassword(
                      watch("code") as number,
                      csrfToken,
                      watch("password") as string,
                      setIsModalOpen,
                      reset
                    );
                  }}
                  loading={isLoadingChangePassword}
                  disabled={!watch("code") || watch("code")?.length < 6}
                >
                  Verify email
                </Button>
              </Stack>
              {!!errorMessage && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  marginY="32px"
                >
                  <Typography type="TextSM" color="red">
                    Your OTP code is incorrect or expired! Please enter it
                    again.
                  </Typography>
                </Stack>
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                marginY="32px"
              >
                <Typography type="TextSM" fontWeight={600}>
                  Didn’t receive the email?
                </Typography>
                {timeCount === 0 ? (
                  <Typography
                    type="TextSM"
                    fontWeight={600}
                    color={primary[400]}
                    marginLeft={1}
                    onClick={() => {
                      // onSubmit(watch());
                    }}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    Click to resend
                  </Typography>
                ) : (
                  <>
                    <Typography
                      type="TextSM"
                      fontWeight={600}
                      color={primary[400]}
                      marginLeft={1}
                    >
                      {`Click to resend after ${timeCount}s`}
                    </Typography>
                    <ClockFastForwardIcon
                      fontSize="small"
                      sx={{
                        color: primary[400],
                      }}
                    />
                  </>
                )}
              </Stack>
            </Grid>
          </Box>
        }
      />
    </FormContainerControlled>
  );
};
